import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { getExternalLayerTimeConfig } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/tennesseeNationalGuardLayers';
import {
  applyExternalLayerTimeWindow,
  collectFeatureLayers,
  discoverLayerTimeBounds,
  formatTimeRangeLabel,
  type LayerTimeApplyMode,
} from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useExternalLayerTimeExtent';
import { sliderValueToDate } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useSharedTnGuardLayerTimeRange';
import { resolveTimeFilterField } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/tennesseeNationalGuardLayers';
import type { SelectedLayerForAI } from './prefetchLayerFeaturesForAI';

const POLL_MS = 200;
const MAX_POLLS = 20;

type LayerTarget = {
  layerId: string;
  preferredDateField: string | null;
  mode: LayerTimeApplyMode;
  dateField: string | null;
};

export type SelectedLayersTimeRange = {
  start: Date | null;
  end: Date | null;
  setStart: (date: Date) => void;
  setEnd: (date: Date) => void;
  setRangeFromSlider: (startValue: number, endValue: number) => void;
  fullStart: Date | undefined;
  fullEnd: Date | undefined;
  status: 'idle' | 'loading' | 'ready' | 'unavailable';
  message?: string;
  clearMapFilters: () => void;
};

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function clampDate(date: Date, min: Date, max: Date): Date {
  const t = date.getTime();
  if (t < min.getTime()) return new Date(min);
  if (t > max.getTime()) return new Date(max);
  return date;
}

function stripTimeFields(layer: SelectedLayerForAI): SelectedLayerForAI {
  const next = { ...layer };
  delete next.timeExtent;
  delete next.timeFilterLabel;
  delete next.timeFilterField;
  return next;
}

function stampTimeFields(
  layers: SelectedLayerForAI[],
  start: Date,
  end: Date
): SelectedLayerForAI[] {
  const label = formatTimeRangeLabel(start, end);
  const startIso = start.toISOString();
  const endIso = end.toISOString();
  return layers.map((layer) => {
    const timeFilterField = resolveTimeFilterField(layer);
    return {
      ...layer,
      timeExtent: { startIso, endIso },
      timeFilterLabel: label,
      ...(timeFilterField ? { timeFilterField } : {}),
    };
  });
}

/**
 * Discover a shared time window for selected AI layers, apply it to the map,
 * and stamp timeExtent / timeFilterLabel onto the selection for Pratus AI.
 */
export function useSelectedLayersTimeRange(options: {
  enabled: boolean;
  selectedLayers: SelectedLayerForAI[];
  onSelectedLayersChange: (layers: SelectedLayerForAI[]) => void;
}): SelectedLayersTimeRange {
  const { enabled, selectedLayers, onSelectedLayersChange } = options;
  const {
    getAddedExternalLayers,
    isExternalLayersReady,
    externalLayerTimeFilterById,
    setExternalLayerTimeFilter,
    setLayerDefinitionExpressionTimePart,
  } = useUscgMapContext();

  const onChangeRef = useRef(onSelectedLayersChange);
  onChangeRef.current = onSelectedLayersChange;
  const selectedRef = useRef(selectedLayers);
  selectedRef.current = selectedLayers;
  const getAddedExternalLayersRef = useRef(getAddedExternalLayers);
  getAddedExternalLayersRef.current = getAddedExternalLayers;

  // Stable across timeExtent stamps — only membership of external layer ids.
  const externalIdSignature = (() => {
    const ids: string[] = [];
    const seen = new Set<string>();
    for (const layer of selectedLayers) {
      if (layer.kind !== 'external') continue;
      if (seen.has(layer.id)) continue;
      seen.add(layer.id);
      ids.push(layer.id);
    }
    return ids.join(',');
  })();
  const targetKey = externalIdSignature;
  const externalIds = useMemo(
    () => (targetKey ? targetKey.split(',') : []),
    [targetKey]
  );

  const [targets, setTargets] = useState<LayerTarget[]>([]);
  const [fullStart, setFullStart] = useState<Date | undefined>();
  const [fullEnd, setFullEnd] = useState<Date | undefined>();
  const [start, setStartState] = useState<Date | null>(null);
  const [end, setEndState] = useState<Date | null>(null);
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'ready' | 'unavailable'
  >('idle');
  const [message, setMessage] = useState<string | undefined>();

  const restoreRef = useRef<{ startIso?: string; endIso?: string }>({});
  // Seed from stamped selection first, then from map filters.
  for (const layer of selectedLayers) {
    if (layer.timeExtent?.startIso && layer.timeExtent?.endIso) {
      restoreRef.current = {
        startIso: layer.timeExtent.startIso,
        endIso: layer.timeExtent.endIso,
      };
      break;
    }
  }
  if (!restoreRef.current.startIso) {
    for (const id of externalIds) {
      const filter = externalLayerTimeFilterById[id];
      if (filter?.startIso && filter?.endIso) {
        restoreRef.current = {
          startIso: filter.startIso,
          endIso: filter.endIso,
        };
        break;
      }
    }
  }

  const clearMapFilters = useCallback(() => {
    for (const id of externalIds) {
      setExternalLayerTimeFilter(id, null);
      setLayerDefinitionExpressionTimePart(id, null);
    }
  }, [
    externalIds,
    setExternalLayerTimeFilter,
    setLayerDefinitionExpressionTimePart,
  ]);

  useEffect(() => {
    if (!enabled) {
      setTargets([]);
      setFullStart(undefined);
      setFullEnd(undefined);
      setStartState(null);
      setEndState(null);
      setStatus('idle');
      setMessage(undefined);
      return;
    }

    let cancelled = false;
    const ids = targetKey ? targetKey.split(',') : [];

    const waitForFeatureLayers = async (
      layerId: string
    ): Promise<FeatureLayer[]> => {
      for (let i = 0; i < MAX_POLLS; i++) {
        if (cancelled) return [];
        const layers = getAddedExternalLayersRef.current(layerId);
        const featureLayers = collectFeatureLayers(layers);
        if (featureLayers.length > 0) return featureLayers;
        await sleep(POLL_MS);
      }
      return collectFeatureLayers(getAddedExternalLayersRef.current(layerId));
    };

    const discover = async () => {
      if (ids.length === 0) {
        setTargets([]);
        setFullStart(undefined);
        setFullEnd(undefined);
        setStartState(null);
        setEndState(null);
        setStatus('unavailable');
        setMessage('Select at least one ArcGIS layer to filter by datetime.');
        return;
      }

      setStatus('loading');
      setMessage(undefined);

      if (!isExternalLayersReady) {
        return;
      }

      const nextTargets: LayerTarget[] = [];
      let unionStart: Date | null = null;
      let unionEnd: Date | null = null;

      for (const layerId of ids) {
        const selected = selectedRef.current.find(
          (l) => l.kind === 'external' && l.id === layerId
        );
        const config = selected
          ? getExternalLayerTimeConfig({
              arcgisItemId: selected.arcgisItemId,
              name: selected.name,
              serviceUrl: selected.serviceUrl,
            })
          : null;
        const preferredDateField = config?.preferredDateField ?? null;
        const featureLayers = await waitForFeatureLayers(layerId);
        if (cancelled) return;
        const bounds = await discoverLayerTimeBounds(
          featureLayers,
          preferredDateField
        );
        if (cancelled) return;
        if (!bounds.ok) continue;

        nextTargets.push({
          layerId,
          preferredDateField,
          mode: bounds.mode,
          dateField: bounds.dateField,
        });
        if (!unionStart || bounds.fullStart.getTime() < unionStart.getTime()) {
          unionStart = bounds.fullStart;
        }
        if (!unionEnd || bounds.fullEnd.getTime() > unionEnd.getTime()) {
          unionEnd = bounds.fullEnd;
        }
      }

      if (cancelled) return;

      if (!unionStart || !unionEnd || nextTargets.length === 0) {
        setTargets([]);
        setFullStart(undefined);
        setFullEnd(undefined);
        setStartState(null);
        setEndState(null);
        setStatus('unavailable');
        setMessage(
          'Time info unavailable. Ensure the selected layers are loaded on the map.'
        );
        return;
      }

      setTargets(nextTargets);
      setFullStart(unionStart);
      setFullEnd(unionEnd);

      const restoreStart = restoreRef.current.startIso
        ? new Date(restoreRef.current.startIso)
        : null;
      const restoreEnd = restoreRef.current.endIso
        ? new Date(restoreRef.current.endIso)
        : null;
      const nextStart =
        restoreStart && !Number.isNaN(restoreStart.getTime())
          ? clampDate(restoreStart, unionStart, unionEnd)
          : unionStart;
      const nextEnd =
        restoreEnd && !Number.isNaN(restoreEnd.getTime())
          ? clampDate(restoreEnd, unionStart, unionEnd)
          : unionEnd;
      setStartState(
        nextStart.getTime() <= nextEnd.getTime() ? nextStart : nextEnd
      );
      setEndState(
        nextStart.getTime() <= nextEnd.getTime() ? nextEnd : nextStart
      );
      setStatus('ready');
    };

    void discover();
    return () => {
      cancelled = true;
    };
    // Intentionally omit getAddedExternalLayers / selectedLayers: stamping
    // timeExtent must not rediscover or flash status=loading (unmounts sliders).
  }, [enabled, targetKey, isExternalLayersReady]);

  const applyToTargets = useCallback(
    (nextStart: Date, nextEnd: Date, applyTargets: LayerTarget[]) => {
      const rangeStart =
        nextStart.getTime() <= nextEnd.getTime() ? nextStart : nextEnd;
      const rangeEnd =
        nextStart.getTime() <= nextEnd.getTime() ? nextEnd : nextStart;

      for (const target of applyTargets) {
        const featureLayers = collectFeatureLayers(
          getAddedExternalLayers(target.layerId)
        );
        applyExternalLayerTimeWindow({
          layerId: target.layerId,
          featureLayers,
          mode: target.mode,
          dateField: target.dateField,
          start: rangeStart,
          end: rangeEnd,
          setExternalLayerTimeFilter,
          setLayerDefinitionExpressionTimePart,
        });
      }

      const stamped = stampTimeFields(
        selectedRef.current,
        rangeStart,
        rangeEnd
      );
      const same =
        stamped.length === selectedRef.current.length &&
        stamped.every((layer, i) => {
          const prev = selectedRef.current[i];
          return (
            layer.timeExtent?.startIso === prev.timeExtent?.startIso &&
            layer.timeExtent?.endIso === prev.timeExtent?.endIso &&
            layer.timeFilterLabel === prev.timeFilterLabel
          );
        });
      if (!same) {
        onChangeRef.current(stamped);
      }
    },
    [
      getAddedExternalLayers,
      setExternalLayerTimeFilter,
      setLayerDefinitionExpressionTimePart,
    ]
  );

  useEffect(() => {
    if (
      !enabled ||
      status !== 'ready' ||
      !start ||
      !end ||
      targets.length === 0
    )
      return;
    applyToTargets(start, end, targets);
  }, [enabled, status, start, end, targets, applyToTargets]);

  // When newly selected layers are added while filter is on, restamp them.
  useEffect(() => {
    if (!enabled || status !== 'ready' || !start || !end) return;
    const missing = selectedLayers.some(
      (l) => !l.timeExtent?.startIso || !l.timeExtent?.endIso
    );
    if (missing) {
      onChangeRef.current(stampTimeFields(selectedLayers, start, end));
    }
  }, [enabled, status, start, end, selectedLayers]);

  const setStart = useCallback(
    (date: Date) => {
      if (!fullStart || !fullEnd) return;
      const clamped = clampDate(date, fullStart, fullEnd);
      setStartState(clamped);
      setEndState((prevEnd) => {
        if (!prevEnd) return clamped;
        return prevEnd.getTime() < clamped.getTime() ? clamped : prevEnd;
      });
    },
    [fullStart, fullEnd]
  );

  const setEnd = useCallback(
    (date: Date) => {
      if (!fullStart || !fullEnd) return;
      const clamped = clampDate(date, fullStart, fullEnd);
      setEndState(clamped);
      setStartState((prevStart) => {
        if (!prevStart) return clamped;
        return prevStart.getTime() > clamped.getTime() ? clamped : prevStart;
      });
    },
    [fullStart, fullEnd]
  );

  const setRangeFromSlider = useCallback(
    (startValue: number, endValue: number) => {
      if (!fullStart || !fullEnd) return;
      const a = sliderValueToDate(startValue, fullStart, fullEnd);
      const b = sliderValueToDate(endValue, fullStart, fullEnd);
      if (a.getTime() <= b.getTime()) {
        setStartState(a);
        setEndState(b);
      } else {
        setStartState(b);
        setEndState(a);
      }
    },
    [fullStart, fullEnd]
  );

  return useMemo(
    () => ({
      start,
      end,
      setStart,
      setEnd,
      setRangeFromSlider,
      fullStart,
      fullEnd,
      status,
      message,
      clearMapFilters,
    }),
    [
      start,
      end,
      setStart,
      setEnd,
      setRangeFromSlider,
      fullStart,
      fullEnd,
      status,
      message,
      clearMapFilters,
    ]
  );
}

export { stripTimeFields, stampTimeFields };
